import type {
  ModalProps,
  RenderTreeNodePayload,
  TreeNodeData,
} from '@mantine/core';
import {Modal, Tree, useTree} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDebouncedValue} from '@mantine/hooks';
import {
  IconFolder,
  IconFolderFilled,
  IconFolderOpen,
} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {Textbox} from 'src/design/atoms/Textbox';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetDocumentTreeQuery} from 'src/redux/queries/document';
import {useAlert} from 'src/shared/hooks/useAlert';
import {filterTreeData} from 'src/shared/utils/treeUtils';

export interface DocumentTreeModalProps
  extends Omit<ModalProps, 'onClose' | 'title' | 'children'> {
  opened: boolean;
  onClose: () => void;
  onConfirm: (selectedNodeId: number | null) => void;
  title?: string;
  currentItemName?: string;
  currentItemType?: 'folder' | 'file';
  currentItemId?: number;
}

interface FileIconProps {
  isGroup: boolean;
  expanded: boolean;
}

function FileIcon({isGroup, expanded}: FileIconProps) {
  if (isGroup) {
    return expanded ? (
      <IconFolderOpen
        color="var(--mantine-color-blue-6)"
        size={16}
        stroke={2}
      />
    ) : (
      <IconFolder color="var(--mantine-color-blue-6)" size={16} stroke={2} />
    );
  } else {
    return (
      <IconFolderFilled
        size={16}
        stroke={2}
        color="var(--mantine-color-gray-6)"
      />
    );
  }
}

function Leaf({
  node,
  expanded,
  hasChildren,
  elementProps,
}: RenderTreeNodePayload) {
  return (
    <Group gap={5} {...elementProps}>
      <FileIcon isGroup={hasChildren} expanded={expanded} />
      <span>{node.label}</span>
    </Group>
  );
}

export const DocumentTreeModal: React.FC<DocumentTreeModalProps> = ({
  opened,
  onClose,
  onConfirm,
  title = 'Select New Location',
  currentItemName,
  currentItemType = 'folder',
  currentItemId,
  ...modalProps
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 100);
  const {setAlert} = useAlert();
  const {
    data,
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    refetch,
  } = useGetDocumentTreeQuery(
    {
      isLeaf: false,
    },
    {
      skip: !opened,
    }
  );
  const isLoading = isDataLoading || isDataFetching;

  const treeNodeData = useMemo(() => {
    return (data ?? []) as TreeNodeData[];
  }, [data]);

  const filteredTreeNodeData = useMemo(() => {
    // Create additional filter to exclude current item if currentItemId is provided
    const filterFn = currentItemId
      ? (node: TreeNodeData) => node.value !== currentItemId.toString()
      : undefined;

    return filterTreeData(treeNodeData, debouncedSearchTerm, filterFn);
  }, [debouncedSearchTerm, treeNodeData, currentItemId]);

  const tree = useTree({
    multiple: false,
  });

  useEffect(() => {
    if (opened) {
      void refetch();
      tree.clearSelected();
    }

    tree.expandAllNodes();
  }, [opened]);

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleConfirm = () => {
    const selectedId = tree.selectedState[0];
    if (!selectedId) {
      setAlert('No folder selected.', 'error');
      return;
    }
    onConfirm(parseInt(selectedId));
    onClose();
  };

  const handleCancel = () => {
    setSearchTerm('');
    tree.clearSelected();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={title}
      size="md"
      centered
      {...modalProps}>
      <Modal.Header pos={'sticky'} style={{padding: 0}}>
        <Box w="100%">
          <Text size="sm" c="dimmed" mb="sm">
            Select the new location for{' '}
            <Text component="span" fw={500}>
              {currentItemName}
            </Text>{' '}
            ({currentItemType}). The folder and all subfolders will be moved to
            the selected folder.
          </Text>
          <Textbox
            label="Search"
            name="search"
            value={searchTerm}
            onChangeCallback={handleSearchTerm}
            autoComplete={false}
          />
          <Divider />
        </Box>
      </Modal.Header>

      {isLoading ? (
        <TableBodyRender isLoading={isLoading} />
      ) : (
        <Tree
          selectOnClick
          expandOnClick={false}
          data={filteredTreeNodeData}
          renderNode={(payload: RenderTreeNodePayload) => <Leaf {...payload} />}
          mb="md"
          tree={tree}
        />
      )}

      <Modal.Header pos={'sticky'} bottom={0} style={{padding: 0}}>
        <Group justify="end" w="100%">
          <DialogButton onClick={handleCancel}>Cancel</DialogButton>
          <DialogButton
            size="md"
            variant="filled"
            color="blue"
            onClick={handleConfirm}
            disabled={tree.selectedState.length === 0}>
            Move to folder
          </DialogButton>
        </Group>
      </Modal.Header>
    </Modal>
  );
};
